import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  Controller,
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  BillingProviderDTO,
  BillingProviderPtanDTO,
} from 'dtos';
import {
  useGetBillingProvider,
  useUpsertBillingProviderPtan,
} from 'views/EMR/Settings/hooks';
import {
  getSubmitButtonText,
  isNilOrZero,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Payer,
} from 'pages/Dashboard/types/insurance';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import Select from 'core/components/Select';
import Input from 'pages/Dashboard/components/Input';
import {
  BillingProviderLabel,
} from 'pages/Dashboard/pages/Billing/ClaimForm/Info';

export type BillingProviderPtanDialogProps = {
  billingProviderPtan?: BillingProviderPtanDTO | null;
};

type Props = BillingProviderPtanDialogProps & {
  onClose: () => void;
};

type BillingProviderPtanForm = {
  billingProviderPtanId?: number;
  billingProvider?: Pick<BillingProviderDTO, 'billingProviderId'> | null;
  payer?: Partial<Payer> | null;
  ptan: string;
};

function getDefaultValues({
  billingProviderPtanId,
  billingProviderId,
  payerEntryId,
  payerEntrySummary,
  ptan,
}: BillingProviderPtanDTO): BillingProviderPtanForm {
  return {
    billingProviderPtanId,
    billingProvider: isNil(billingProviderId) ? null : { billingProviderId },
    payer: isNil(payerEntryId) ? null : { payerEntryId, payerName: payerEntrySummary?.name ?? '' },
    ptan: ptan ?? '',
  };
}

export default function BillingProviderPtanDialog({
  onClose,
  billingProviderPtan,
}: Props) {
  const isNew = isNil(billingProviderPtan);

  const formProps = useForm<BillingProviderPtanForm>({
    mode: 'onChange',
    defaultValues: getDefaultValues(billingProviderPtan ?? {}),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const { mutateAsync: upsertBillingProviderPtan } = useUpsertBillingProviderPtan();
  const { data: billingProviders } = useGetBillingProvider();
  const notify = useNotify();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: BillingProviderPtanForm) => {
    try {
      await upsertBillingProviderPtan({
        billingProviderPtanId: data.billingProviderPtanId,
        billingProviderId: data.billingProvider?.billingProviderId,
        payerEntryId: data.payer?.payerEntryId,
        ptan: data.ptan.trim(),
      });
      notify({
        variant: 'success',
        message: `PTAN ${isNew ? 'created' : 'updated'} successfully`,
      });
      settingsEventChannel.emit('onBillingProviderPtanChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={(
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="text-lg font-semibold">
              {`${isNew ? 'Add' : 'Edit'} PTAN`}
            </h2>
          </div>
        )}
        closeBtnClasses="!-top-1"
        onClose={onClose}
        onConfirm={noop}
        className="!w-140 populate-form"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col gap-form-row">
            <Controller
              control={control}
              name="billingProvider"
              rules={{ validate: (value) => !isNilOrZero(value?.billingProviderId) || 'Billing Provider is required' }}
              render={({ field }) => (
                <Select<BillingProviderDTO>
                  {...field}
                  id="ptan-billing-provider"
                  className="text-gray-500 text-xs"
                  label="Billing Provider"
                  placeholder="Select"
                  options={billingProviders ?? []}
                  getLabel={BillingProviderLabel}
                  getValue={(option) => option?.billingProviderId ?? ''}
                  required
                />
              )}
            />
            <Controller
              name="payer"
              control={control}
              rules={{ validate: (value) => !isNilOrZero(value?.payerEntryId) || 'Payer is required' }}
              render={({ field, fieldState: { invalid } }) => (
                <InsuranceLookup
                  {...field}
                  error={invalid}
                  id="ptan-payer"
                  label="Payer"
                  value={field?.value?.payerName ?? ''}
                  className="!mb-0"
                  required
                />
              )}
            />
            <Controller
              name="ptan"
              control={control}
              rules={{
                required: 'PTAN is required',
                validate: (value) => value.trim().length > 0 || 'PTAN is required',
              }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  error={invalid}
                  showErrorText={false}
                  id="ptan"
                  label="PTAN"
                  inputProps={{ maxLength: 64 }}
                  required
                />
              )}
            />
          </div>
          <div className="flex gap-2 justify-between">
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
